<script lang="ts">
  import { Icon, type IconName } from '$ui/primitives/Icon';

  let {
    variant = 'error',
    message,
    action,
    dismissible = false,
    ondismiss,
    class: className = ''
  }: {
    variant?: 'error' | 'warning' | 'info' | 'success';
    message: string;
    action?: { label: string; onclick: () => void };
    dismissible?: boolean;
    ondismiss?: () => void;
    class?: string;
  } = $props();

  const iconName = $derived<IconName>(
    variant === 'error'
      ? 'alert-circle'
      : variant === 'warning'
        ? 'alert-triangle'
        : variant === 'success'
          ? 'check-circle'
          : 'info'
  );

  const role = $derived(variant === 'error' ? 'alert' : 'status');
</script>

<div class="banner {className}" data-variant={variant} {role}>
  <span class="icon" aria-hidden="true">
    <Icon name={iconName} size={20} />
  </span>
  <p class="message">{message}</p>
  {#if action}
    <button type="button" class="action" onclick={action.onclick}>
      {action.label}
    </button>
  {/if}
  {#if dismissible}
    <button
      type="button"
      class="dismiss"
      aria-label="Cerrar"
      onclick={() => ondismiss?.()}
    >
      <Icon name="x" size={16} />
    </button>
  {/if}
</div>

<style>
  .banner {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-sm);
    border-left: 3px solid;
    font-size: var(--text-sm);
    line-height: 1.6;
  }

  .banner[data-variant='error'] {
    background: var(--color-danger-tint);
    border-left-color: var(--color-danger);
  }
  .banner[data-variant='warning'] {
    background: var(--color-warning-tint);
    border-left-color: var(--color-warning);
  }
  .banner[data-variant='info'] {
    background: var(--color-info-tint);
    border-left-color: var(--color-info);
  }
  .banner[data-variant='success'] {
    background: var(--color-success-tint);
    border-left-color: var(--color-success);
  }

  .icon {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
  }
  .banner[data-variant='error'] .icon { color: var(--color-danger); }
  .banner[data-variant='warning'] .icon { color: var(--color-warning); }
  .banner[data-variant='info'] .icon { color: var(--color-info); }
  .banner[data-variant='success'] .icon { color: var(--color-success); }

  .message {
    margin: 0;
    flex: 1;
    min-width: 0;
    color: var(--color-text-primary);
  }

  .action {
    flex: 0 0 auto;
    padding: var(--space-2) var(--space-4);
    font-family: inherit;
    font-size: 0.72rem;
    font-weight: var(--weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    background: transparent;
    border: var(--border-width) solid currentColor;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out);
  }

  .banner[data-variant='error'] .action { color: var(--color-danger); }
  .banner[data-variant='warning'] .action { color: var(--color-warning); }
  .banner[data-variant='info'] .action { color: var(--color-info); }
  .banner[data-variant='success'] .action { color: var(--color-success); }

  .action:hover {
    background: var(--color-surface-hover);
  }

  .action:focus-visible,
  .dismiss:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .dismiss:hover {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
  }
</style>